import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createHead } from '@unhead/vue';
import App from './App.vue';
import router from './router';
import i18n from './i18n';
import { setUnauthorizedHandler } from './services/httpClient';
import { sendErrorTelemetry } from './services/telemetry';
import { useAppStore } from './stores/app';

import('./style.css');
import('./style.theme-light.css');

const app = createApp(App);

app.use(createPinia());
// Vue Router (spec 047-blog-section, T0.2): app-wide navigation foundation.
// The legacy wizard stays store-driven inside MainFlow.vue; the router owns the
// URL-addressable surfaces (Blog section, Task Group 2).
app.use(router);
app.use(i18n);
// SEO (spec 045): @unhead/vue manages dynamic <head> meta — title, description,
// Open Graph / Twitter cards, html[lang], and JSON-LD — declared per-component
// via useHead(). Registered before mount so root meta applies on the first frame.
app.use(createHead());

// Global response hook: on 401, clear the session and return to the landing
// step (sign-out) via the Pinia store rather than navigating from the service.
setUnauthorizedHandler(() => {
  const store = useAppStore();
  store.clearExpiredSessionState();
  store.setStep('landing');
});

// Global client-side error telemetry (spec 037): forward Vue component errors,
// uncaught exceptions, and unhandled promise rejections to the backend logger.
app.config.errorHandler = (err, _instance, info) => {
  const e = err as Error;
  sendErrorTelemetry({
    message: e?.message ?? String(err),
    stack: e?.stack,
    component: info,
    kind: 'vue',
  });
  // Preserve default behavior: surface the error in the console for local dev.
  console.error(err);
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    sendErrorTelemetry({
      message: event.message || 'Uncaught error',
      stack: event.error?.stack,
      kind: 'window',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as Error | undefined;
    sendErrorTelemetry({
      message: reason?.message ?? String(event.reason ?? 'Unhandled promise rejection'),
      stack: reason?.stack,
      kind: 'promise',
    });
  });
}

app.mount('#app');
